import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AdminShell } from '../../components/AdminShell';
import { supabase } from '../../lib/supabase';
import {
  ShieldAlert, RefreshCw, Loader2, Search, ChevronRight, X, Globe2,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ---------- shared fetch helper ------------------------------------
const useAuthedFetch = () => {
  return async (path, params = {}) => {
    const { data: { session } } = await supabase.auth.getSession();
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, v);
    });
    const url = `${BACKEND}${path}${qs.toString() ? `?${qs}` : ''}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${session?.access_token}` },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.detail || `HTTP ${res.status}`);
    return body;
  };
};

// ---------- severity chip ------------------------------------------
const SevChip = ({ count = 0, max = 0 }) => {
  if (!count) return <span style={{ color: '#94a3b8' }}>—</span>;
  const bg = max >= 3 ? '#fee2e2' : max === 2 ? '#fef3c7' : '#e0e7ff';
  const fg = max >= 3 ? '#991b1b' : max === 2 ? '#92400e' : '#3730a3';
  return (
    <span style={{
      background: bg, color: fg, padding: '2px 8px', borderRadius: 999,
      fontSize: 11.5, fontWeight: 600,
    }} data-testid="intel-sev-chip">
      {count} · sev {max}
    </span>
  );
};

// ---------- summary strip ------------------------------------------
const SummaryStrip = ({ window: win, setWindow }) => {
  const authedFetch = useAuthedFetch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const refresh = async () => {
    setLoading(true);
    try { setData(await authedFetch('/api/admin/booking-intel/summary', { window: win })); }
    catch (e) { setData({ error: e.message }); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [win]);
  const stats = data && !data.error ? [
    { label: 'Bookings',        value: data.bookings_total },
    { label: 'Flagged',         value: data.bookings_flagged },
    { label: 'Unique IPs',      value: data.unique_ips },
    { label: 'Median wizard',   value: data.median_wizard_seconds != null ? `${data.median_wizard_seconds}s` : '—' },
  ] : [];
  return (
    <div className="panel" style={{ padding: 14, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <select className="inp" value={win} onChange={(e) => setWindow(e.target.value)} data-testid="intel-window-select" style={{ maxWidth: 130 }}>
          <option value="today">Today</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
        </select>
        <button className="btn" onClick={refresh} disabled={loading} data-testid="intel-refresh-summary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {loading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
        <div style={{ display: 'flex', gap: 16, marginLeft: 'auto', flexWrap: 'wrap' }}>
          {stats.map((s) => (
            <div key={s.label} style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>{s.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700 }} data-testid={`intel-stat-${s.label.toLowerCase().replace(/\s+/g,'-')}`}>{s.value ?? '—'}</div>
            </div>
          ))}
        </div>
      </div>
      {data?.error && <div style={{ color: '#b91c1c', fontSize: 12.5, marginTop: 8 }}>{data.error}</div>}
    </div>
  );
};

// ---------- tab 1: live feed ---------------------------------------
const LiveFeed = ({ onOpen }) => {
  const authedFetch = useAuthedFetch();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hoursBack, setHoursBack] = useState(24);
  const [signal, setSignal] = useState('');
  const [country, setCountry] = useState('');
  const [minSecs, setMinSecs] = useState('');
  const load = async () => {
    setLoading(true); setError('');
    try {
      const p = { hours_back: hoursBack };
      if (signal) p.signal = signal;
      if (country) p.country = country;
      if (minSecs !== '') p.min_secs = Number(minSecs) || 0;
      const d = await authedFetch('/api/admin/booking-intel/feed', p);
      setRows(d.rows || []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  return (
    <div>
      <div className="panel" style={{ padding: 12, marginBottom: 12, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field" style={{ minWidth: 120 }}>
          <label style={{ fontSize: 11.5, color: '#64748b' }}>Hours back</label>
          <input className="inp" type="number" min={1} max={720} value={hoursBack} onChange={(e) => setHoursBack(Number(e.target.value)||24)} data-testid="intel-feed-hours" />
        </div>
        <div className="field" style={{ minWidth: 200 }}>
          <label style={{ fontSize: 11.5, color: '#64748b' }}>Signal</label>
          <input className="inp" value={signal} onChange={(e) => setSignal(e.target.value)} placeholder="e.g. sub_3s_wizard" data-testid="intel-feed-signal" />
        </div>
        <div className="field" style={{ minWidth: 90 }}>
          <label style={{ fontSize: 11.5, color: '#64748b' }}>Country</label>
          <input className="inp" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="GB" maxLength={2} data-testid="intel-feed-country" />
        </div>
        <div className="field" style={{ minWidth: 110 }}>
          <label style={{ fontSize: 11.5, color: '#64748b' }}>Min secs</label>
          <input className="inp" type="number" min={0} value={minSecs} onChange={(e) => setMinSecs(e.target.value)} placeholder="0" data-testid="intel-feed-minsecs" />
        </div>
        <button className="btn primary" onClick={load} disabled={loading} data-testid="intel-feed-apply">
          {loading ? <Loader2 size={14} className="spin" /> : <Search size={14} />} Apply
        </button>
      </div>
      {error && <div style={{ color: '#b91c1c', fontSize: 12.5, marginBottom: 10 }}>{error}</div>}
      <div className="panel" style={{ padding: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px' }}>Sig</th>
              <th style={{ padding: '10px 12px' }}>When</th>
              <th style={{ padding: '10px 12px' }}>Shop</th>
              <th style={{ padding: '10px 12px' }}>Channel</th>
              <th style={{ padding: '10px 12px' }}>IP</th>
              <th style={{ padding: '10px 12px' }}>Country</th>
              <th style={{ padding: '10px 12px' }}>Device</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>Wiz(s)</th>
              <th style={{ padding: '10px 12px' }}>Signals</th>
              <th />
            </tr>
          </thead>
          <tbody data-testid="intel-feed-tbody">
            {rows.length === 0 && !loading && (
              <tr><td colSpan={10} style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>No bookings in this window.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.booking_id} style={{ borderTop: '1px solid #e2e8f0', cursor: 'pointer' }} onClick={() => onOpen(r.booking_id)} data-testid={`intel-feed-row-${r.booking_id}`}>
                <td style={{ padding: '8px 12px' }}><SevChip count={r.signal_count} max={r.max_severity} /></td>
                <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{new Date(r.created_at).toLocaleString('en-GB')}</td>
                <td style={{ padding: '8px 12px' }}>{r.shop_name || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                <td style={{ padding: '8px 12px' }}>{r.channel}</td>
                <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{r.ip || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                <td style={{ padding: '8px 12px' }}>{r.country || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                <td style={{ padding: '8px 12px' }}>{r.device ? `${r.device} · ${r.browser || ''}` : <span style={{ color: '#94a3b8' }}>—</span>}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', color: (r.wizard_seconds != null && r.wizard_seconds < 3) ? '#b91c1c' : undefined, fontWeight: (r.wizard_seconds != null && r.wizard_seconds < 3) ? 700 : undefined }}>{r.wizard_seconds ?? '—'}</td>
                <td style={{ padding: '8px 12px', color: '#475569', fontSize: 11.5 }}>{(r.signals || []).join(', ') || '—'}</td>
                <td style={{ padding: '8px 12px' }}><ChevronRight size={14} color="#94a3b8" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- tab 2: signals -----------------------------------------
const SignalsFeed = ({ onOpen }) => {
  const authedFetch = useAuthedFetch();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [showReviewed, setShowReviewed] = useState(false);
  const [error, setError] = useState('');
  const load = async () => {
    setError('');
    try { setData(await authedFetch('/api/admin/booking-intel/signals', { window_days: days, reviewed: showReviewed })); }
    catch (e) { setError(e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, showReviewed]);
  const review = async (signalId, verdict) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await fetch(`${BACKEND}/api/admin/booking-intel/signals/${signalId}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ verdict }),
      });
      await load();
    } catch (e) { setError(e.message); }
  };
  return (
    <div>
      <div className="panel" style={{ padding: 12, marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-end' }}>
        <div className="field">
          <label style={{ fontSize: 11.5, color: '#64748b' }}>Window (days)</label>
          <input className="inp" type="number" min={1} max={90} value={days} onChange={(e) => setDays(Number(e.target.value) || 7)} data-testid="intel-signals-days" style={{ width: 90 }} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#334155' }}>
          <input type="checkbox" checked={showReviewed} onChange={(e) => setShowReviewed(e.target.checked)} data-testid="intel-signals-reviewed-toggle" /> Show reviewed
        </label>
      </div>
      {error && <div style={{ color: '#b91c1c', fontSize: 12.5, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {(data?.groups || []).length === 0 && <div className="panel" style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>No {showReviewed ? '' : 'unreviewed '}signals in this window.</div>}
        {(data?.groups || []).map((g) => (
          <details key={g.signal} className="panel" style={{ padding: 12 }} data-testid={`intel-signal-group-${g.signal}`}>
            <summary style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
              <SevChip count={g.count} max={g.severity} />
              <strong>{g.signal}</strong>
              <span style={{ color: '#64748b', fontSize: 12 }}>{g.count} rows</span>
            </summary>
            <div style={{ marginTop: 10 }}>
              {g.rows.map((r) => (
                <div key={r.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 0', borderTop: '1px solid #f1f5f9', fontSize: 12.5 }}>
                  <span style={{ color: '#64748b', minWidth: 130 }}>{new Date(r.created_at).toLocaleString('en-GB')}</span>
                  <button onClick={() => onOpen(r.booking_id)} className="btn" style={{ padding: '2px 8px', fontSize: 12 }}>Open booking</button>
                  <code style={{ fontSize: 11, color: '#475569', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{JSON.stringify(r.detail || {})}</code>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                    {!r.reviewed_at ? (
                      <>
                        <button className="btn" style={{ padding: '2px 8px', fontSize: 12 }} onClick={() => review(r.id, 'legitimate')} data-testid={`intel-review-legit-${r.id}`}>Legit</button>
                        <button className="btn" style={{ padding: '2px 8px', fontSize: 12 }} onClick={() => review(r.id, 'suspicious')} data-testid={`intel-review-susp-${r.id}`}>Suspicious</button>
                        <button className="btn primary" style={{ padding: '2px 8px', fontSize: 12 }} onClick={() => review(r.id, 'confirmed_abuse')} data-testid={`intel-review-abuse-${r.id}`}>Abuse</button>
                      </>
                    ) : (
                      <span style={{ fontSize: 11, color: '#64748b' }}>reviewed · {r.verdict}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
};

// ---------- tab 3: IP rollup ---------------------------------------
const IpRollup = () => {
  const authedFetch = useAuthedFetch();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [error, setError] = useState('');
  const load = async () => {
    setError('');
    try { setData(await authedFetch('/api/admin/booking-intel/ip-rollup', { window_days: days, limit: 50 })); }
    catch (e) { setError(e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);
  return (
    <div>
      <div className="panel" style={{ padding: 12, marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-end' }}>
        <div className="field">
          <label style={{ fontSize: 11.5, color: '#64748b' }}>Window (days)</label>
          <input className="inp" type="number" min={1} max={180} value={days} onChange={(e) => setDays(Number(e.target.value) || 30)} data-testid="intel-ip-days" style={{ width: 90 }} />
        </div>
      </div>
      {error && <div style={{ color: '#b91c1c', fontSize: 12.5, marginBottom: 10 }}>{error}</div>}
      <div className="panel" style={{ padding: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px' }}>IP</th>
              <th style={{ padding: '10px 12px' }}>Country</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>Bookings</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>Shops touched</th>
              <th style={{ padding: '10px 12px' }}>First seen</th>
              <th style={{ padding: '10px 12px' }}>Last seen</th>
            </tr>
          </thead>
          <tbody data-testid="intel-iprollup-tbody">
            {(data?.rows || []).length === 0 && <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>No IPs recorded in this window.</td></tr>}
            {(data?.rows || []).map((r) => (
              <tr key={r.ip} style={{ borderTop: '1px solid #e2e8f0' }} data-testid={`intel-iprollup-row-${r.ip.replace(/[.:]/g, '-')}`}>
                <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{r.ip}</td>
                <td style={{ padding: '8px 12px' }}>{r.country || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: r.total >= 10 ? 700 : undefined }}>{r.total}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', color: r.shops_touched > 1 ? '#b91c1c' : undefined, fontWeight: r.shops_touched > 1 ? 700 : undefined }}>{r.shops_touched}</td>
                <td style={{ padding: '8px 12px' }}>{new Date(r.first_seen).toLocaleDateString('en-GB')}</td>
                <td style={{ padding: '8px 12px' }}>{new Date(r.last_seen).toLocaleDateString('en-GB')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- tab 4: booking detail ----------------------------------
const BookingDetail = ({ bookingId, onClose }) => {
  const authedFetch = useAuthedFetch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    (async () => {
      try { setData(await authedFetch(`/api/admin/booking-intel/booking/${bookingId}`)); }
      catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [bookingId]);
  if (loading) return <div className="panel" style={{ padding: 20, textAlign: 'center' }}><Loader2 className="spin" /></div>;
  if (error) return <div className="panel" style={{ padding: 20, color: '#b91c1c' }}>{error}</div>;
  const { booking, context, signals, events, ip_intel } = data;
  return (
    <div className="panel" style={{ padding: 18 }} data-testid="intel-booking-detail">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Booking {booking.id.slice(0, 8)}…</h3>
        <button className="btn" onClick={onClose} data-testid="intel-detail-close" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}><X size={14} /> Close</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div>
          <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#334155' }}>Booking</h4>
          <div style={{ fontSize: 12.5, lineHeight: 1.7 }}>
            <div><b>Shop</b> {(booking.shops || {}).name}</div>
            <div><b>Customer</b> {(booking.customers || {}).name} · {(booking.customers || {}).email} · <code>{(booking.customers || {}).phone}</code></div>
            <div><b>Service</b> {(booking.services || {}).name} ({(booking.services || {}).duration_min}min · £{(booking.services || {}).price})</div>
            <div><b>Barber</b> {(booking.barbers || {}).name}</div>
            <div><b>Starts</b> {new Date(booking.starts_at).toLocaleString('en-GB')}</div>
            <div><b>Status</b> {booking.status} · {booking.payment_status}</div>
            <div><b>Channel</b> {booking.channel}</div>
            <div><b>Booking #</b> {booking.booking_number}</div>
          </div>
          <h4 style={{ margin: '18px 0 8px', fontSize: 13, color: '#334155' }}>Audit trail</h4>
          <div style={{ fontSize: 12, maxHeight: 220, overflow: 'auto', background: '#f8fafc', padding: 8, borderRadius: 6 }}>
            {events.map((e, i) => (
              <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid #e2e8f0' }}>
                <span style={{ color: '#64748b' }}>{new Date(e.created_at).toLocaleString('en-GB')}</span>
                {' '}<b>{e.event_type}</b> ({e.actor_role})
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#334155' }}>Context</h4>
          {!context && <div style={{ fontSize: 12.5, color: '#94a3b8' }}>No context row — the beacon didn't fire (customer closed the tab immediately after booking).</div>}
          {context && (
            <div style={{ fontSize: 12.5, lineHeight: 1.7 }}>
              <div><b>IP</b> <code>{context.ip_address || '—'}</code>{ip_intel && <> · {ip_intel.city}, {ip_intel.country} · {ip_intel.org}{ip_intel.is_datacenter && <span style={{ color: '#b91c1c' }}> · datacenter</span>}</>}</div>
              <div><b>UA</b> <span style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>{context.user_agent}</span></div>
              <div><b>Device</b> {context.ua_device_type} · {context.ua_browser} · {context.ua_os}</div>
              <div><b>Wizard time</b> <span style={{ color: context.wizard_seconds != null && context.wizard_seconds < 3 ? '#b91c1c' : undefined, fontWeight: context.wizard_seconds != null && context.wizard_seconds < 3 ? 700 : undefined }}>{context.wizard_seconds != null ? `${context.wizard_seconds}s` : '—'}</span></div>
              <div><b>Screen</b> {context.screen_w} × {context.screen_h}</div>
              <div><b>Language</b> {context.language}</div>
              <div><b>TZ offset</b> {context.timezone_offset_min}min</div>
              <div><b>Referrer</b> {context.referrer || '—'}</div>
              <div><b>UTM</b> {context.utm_source || '—'} / {context.utm_medium || '—'} / {context.utm_campaign || '—'}</div>
            </div>
          )}
          <h4 style={{ margin: '18px 0 8px', fontSize: 13, color: '#334155' }}>Abuse signals</h4>
          {signals.length === 0 && <div style={{ fontSize: 12.5, color: '#94a3b8' }}>None.</div>}
          {signals.map((s) => (
            <div key={s.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 6, marginBottom: 6, fontSize: 12.5 }}>
              <SevChip count={1} max={s.severity} />
              <b>{s.signal}</b>
              <code style={{ fontSize: 11, color: '#475569' }}>{JSON.stringify(s.detail || {})}</code>
              {s.reviewed_at && <span style={{ color: '#64748b', marginLeft: 'auto' }}>{s.review_verdict}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------- page shell + tabs --------------------------------------
const TABS = [
  { key: 'feed',    label: 'Live feed' },
  { key: 'signals', label: 'Abuse signals' },
  { key: 'ip',      label: 'IP rollups' },
];

const BookingIntelInner = () => {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') || 'feed');
  const [window, setWindow] = useState('today');
  const [openBookingId, setOpenBookingId] = useState(params.get('booking') || null);

  useEffect(() => {
    const p = new URLSearchParams(params);
    p.set('tab', tab);
    if (openBookingId) p.set('booking', openBookingId); else p.delete('booking');
    setParams(p, { replace: true });
    // eslint-disable-next-line
  }, [tab, openBookingId]);

  return (
    <main className="main">
      <div className="topbar">
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShieldAlert size={18} /> Booking intel
          </h2>
          <div className="sub">
            Per-booking IP, device, UA, timing, UTM. Auto-flagged for abuse.
          </div>
        </div>
      </div>

      <div className="body">
        <SummaryStrip window={window} setWindow={setWindow} />

        {openBookingId ? (
          <BookingDetail bookingId={openBookingId} onClose={() => setOpenBookingId(null)} />
        ) : (
          <>
            <div style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: '1px solid #e2e8f0' }}>
              {TABS.map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)}
                  data-testid={`intel-tab-${t.key}`}
                  style={{
                    padding: '10px 16px', border: 'none', background: 'transparent',
                    borderBottom: `2px solid ${tab === t.key ? '#0f172a' : 'transparent'}`,
                    fontWeight: tab === t.key ? 600 : 500,
                    color: tab === t.key ? '#0f172a' : '#64748b',
                    cursor: 'pointer',
                  }}>
                  {t.label}
                </button>
              ))}
            </div>
            {tab === 'feed'    && <LiveFeed    onOpen={setOpenBookingId} />}
            {tab === 'signals' && <SignalsFeed onOpen={setOpenBookingId} />}
            {tab === 'ip'      && <IpRollup />}
          </>
        )}
      </div>
    </main>
  );
};

export default function BookingIntel() {
  return <AdminShell><BookingIntelInner /></AdminShell>;
}
